// Runtime flag para modo demo. Persiste en localStorage (no en build env
// var) para que el MISMO bundle prod pueda estar demo-OFF para Juan y
// demo-ON solo en el device de Ale. Base del paquete demo completo (que
// después reusará el flag para métricas mock, pacientes falsos, etc.).
//
// Activación:
//   - `gonix.jeroz.dev/?demo=1` → setea el flag y limpia el query param.
//   - `gonix.jeroz.dev/?demo=0` → limpia el flag + session snapshots
//     capturados (cleanup de exit del modo demo).
//   - Sin `?demo`, el flag persiste como estaba (permite bookmarkear el
//     dominio sin re-especificar).
//
// El bootstrap se llama desde main.tsx PRE-React mount para garantizar
// que cualquier componente que lea `isDemoMode()` en su primer render
// vea el valor correcto.

import { clearStoredSessions } from "./sessionSnapshots";

const DEMO_MODE_KEY = "gonix_demo_mode";

/** Lee el flag de demo del localStorage. `false` cuando localStorage no
 *  existe (SSR, sandboxed iframe) o el valor no es "true". */
export function isDemoMode(): boolean {
  if (typeof window === "undefined") return false;
  try {
    return window.localStorage.getItem(DEMO_MODE_KEY) === "true";
  } catch {
    // localStorage bloqueado (private mode, storage full) → tratamos
    // como off. Sin persist, cada visita arranca sin demo.
    return false;
  }
}

/** Setea o limpia el flag. Al salir del modo demo, LIMPIA también las
 *  session snapshots capturadas — coherente con "cerrar la sesión de
 *  demo": no queremos tokens huérfanos si el device pasa a otro uso. */
export function setDemoMode(enabled: boolean): void {
  if (typeof window === "undefined") return;
  try {
    if (enabled) {
      window.localStorage.setItem(DEMO_MODE_KEY, "true");
    } else {
      window.localStorage.removeItem(DEMO_MODE_KEY);
      clearStoredSessions();
    }
  } catch {
    // Storage bloqueado — no-op silencioso.
  }
}

/** Bootstrap del flag desde el query param `?demo=1|0`. Se llama una vez
 *  desde main.tsx antes de que React monte. Si el query param está
 *  presente, actualiza el flag y quita el param de la URL con
 *  history.replaceState (para que el usuario no vea el `?demo=X` en la
 *  address bar tras la primera visita). */
export function bootstrapDemoModeFromUrl(): void {
  if (typeof window === "undefined") return;

  const params = new URLSearchParams(window.location.search);
  if (!params.has("demo")) return;

  const value = params.get("demo");
  setDemoMode(value === "1");

  // Limpia el query param de la URL sin recargar. Preserva pathname +
  // hash + otros params que no sean `demo`.
  try {
    const url = new URL(window.location.href);
    url.searchParams.delete("demo");
    window.history.replaceState({}, "", url.toString());
  } catch {
    // Si URL/history no están disponibles (raro), dejamos el query en la
    // barra — no afecta funcionalidad.
  }
}
